import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';

const Services = () => {
  const location = useLocation();

  // Handle hash navigation - scroll to section when hash is present
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  const services = [
    {
      id: 'web-development',
      title: 'Website Development',
      description: 'We build professional, high-performance websites that establish your digital presence and drive business results. Our development approach prioritizes user experience, SEO optimization, and scalability.',
      benefits: [
        'Corporate websites and business portals',
        'Responsive, mobile-first design',
        'SEO-optimized architecture',
        'Fast loading and performance tuned',
        'Content management system integration',
        'Ongoing support and maintenance'
      ],
      image: 'https://images.unsplash.com/photo-1649698145660-d30f91023b52?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxzb2Z0d2FyZSUyMGRldmVsb3BtZW50JTIwY29kZSUyMGxhcHRvcCUyMHNjcmVlbnxlbnwwfHx8fDE3NzcyMTg1OTY5fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      id: 'hosting',
      title: 'Website Hosting & Deployment',
      description: 'Reliable, secure hosting solutions that keep your website running smoothly. We handle all technical aspects so you can focus on your business.',
      benefits: [
        'Secure hosting infrastructure',
        'Domain registration and DNS management',
        'SSL certificate setup and renewal',
        'Professional email configuration',
        'Regular backups and disaster recovery',
        'Performance monitoring and optimization'
      ],
      image: 'https://images.pexels.com/photos/17323801/pexels-photo-17323801.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      id: 'software',
      title: 'Custom Software Development',
      description: 'Tailored software solutions that automate processes, improve efficiency, and solve unique business challenges. We build systems that work the way you work.',
      benefits: [
        'Business process automation',
        'Enterprise web applications',
        'API development and integration',
        'Database design and management',
        'Legacy system modernization',
        'Third-party system integration'
      ],
      image: 'https://images.unsplash.com/photo-1555066931-4365d14bab8c?auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 'enterprise',
      title: 'Enterprise Engineering Solutions',
      description: 'Deep expertise in enterprise product lifecycle management, digital thread integration, and smart manufacturing systems. We help organizations streamline complex engineering data and processes.',
      benefits: [
        'Product lifecycle management platforms',
        'Engineering data integration and migration',
        'Digital thread and traceability solutions',
        'Configuration and change management systems',
        'Enterprise visualization portals',
        'Industrial IoT and connected solutions'
      ],
      image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=800&q=80'
    },
    {
      id: 'marketing',
      title: 'Digital Marketing & Growth',
      description: 'Strategic digital marketing services that increase visibility, generate leads, and drive measurable business growth. We focus on results, not vanity metrics.',
      benefits: [
        'Search engine optimization (SEO)',
        'Performance marketing campaigns',
        'Lead generation strategies',
        'Analytics and conversion tracking',
        'Content strategy and development',
        'Social media management'
      ],
      image: 'https://images.pexels.com/photos/5833273/pexels-photo-5833273.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    }
  ];

  return (
    <>
      {/* Page Header */}
      <section className="page-header" data-testid="services-header">
        <div className="container">
          <h1>Our Services</h1>
          <p>
            Comprehensive technology solutions designed to drive your business forward.
          </p>
        </div>
      </section>

      {/* Services Detailed */}
      <section className="section" data-testid="services-detailed-section">
        <div className="container">
          <div className="services-detailed">
            {services.map((service, index) => (
              <div 
                key={service.id} 
                className="service-section" 
                id={service.id}
                data-testid={`service-${service.id}`}
              >
                <div className="service-section-content">
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <ul className="service-benefits">
                    {service.benefits.map((benefit, bIndex) => (
                      <li key={bIndex}>
                        <CheckCircle size={18} />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  <Link 
                    to="/contact" 
                    className="btn btn-primary"
                    data-testid={`service-${service.id}-cta`}
                  >
                    Discuss Your Project
                  </Link>
                </div>
                <div className="service-image">
                  <img src={service.image} alt={service.title} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="cta-banner" data-testid="services-cta-section">
        <div className="container">
          <h2>Ready to Get Started?</h2>
          <p>
            Let's discuss your project requirements and find the right solution for your business.
          </p>
          <Link to="/contact" className="btn btn-white" data-testid="services-cta-btn">
            Request a Free Consultation
          </Link>
        </div>
      </section>
    </>
  );
};

export default Services;
